import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { AdminPageHeader } from "@/components/admin/crud-helpers";
import { StatsStrip } from "@/components/admin/stats-strip";
import { ProviderTestButton } from "@/components/admin/provider-test-button";
import { testHostingProvider } from "@/lib/provider-tests.functions";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ExternalLink, Server, CheckCircle2, FlaskConical, Percent, Search, Cloud, HardDrive } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/hosting-providers")({
  component: HostingProvidersAdmin,
});

type ProviderId =
  | "hetzner"
  | "time4vps"
  | "evoxt"
  | "gcp"
  | "aws"
  | "namecheap"
  | "radwebhosting";

type FieldKey =
  | "api_token"
  | "api_key"
  | "api_secret"
  | "username"
  | "client_id"
  | "project_id"
  | "region"
  | "endpoint_url";

type ProviderConfig = {
  enabled: boolean;
  sandbox: boolean;
  markup_percent: number;
  api_token: string;
  api_key: string;
  api_secret: string;
  username: string;
  client_id: string;
  project_id: string;
  region: string;
  endpoint_url: string;
};

type Kind = "vps" | "hosting" | "cloud";

type ProvidersSettings = {
  default_vps: ProviderId;
  default_hosting: ProviderId;
  default_cloud: ProviderId;
  providers: Record<ProviderId, ProviderConfig>;
};

const PROVIDER_META: Record<ProviderId, {
  name: string;
  tagline: string;
  kinds: Kind[];
  docsUrl: string;
  fields: FieldKey[];
}> = {
  hetzner: {
    name: "Hetzner Cloud",
    tagline: "EU-based cloud VPS with great price/perf. Simple REST API + snapshots.",
    kinds: ["vps", "cloud"],
    docsUrl: "https://docs.hetzner.cloud/",
    fields: ["api_token", "region"],
  },
  time4vps: {
    name: "Time4VPS",
    tagline: "Affordable Linux & Windows VPS in EU. WHMCS/REST API for reseller automation.",
    kinds: ["vps"],
    docsUrl: "https://www.time4vps.com/api-documentation/",
    fields: ["api_key", "api_secret"],
  },
  evoxt: {
    name: "Evoxt",
    tagline: "Global VPS network with API provisioning across 15+ regions.",
    kinds: ["vps"],
    docsUrl: "https://evoxt.com/api/",
    fields: ["api_key", "region"],
  },
  gcp: {
    name: "Google Cloud",
    tagline: "Compute Engine VMs + Cloud Run. Use a service account for provisioning.",
    kinds: ["cloud", "vps"],
    docsUrl: "https://cloud.google.com/compute/docs/reference/rest/v1",
    fields: ["api_key", "project_id", "region"],
  },
  aws: {
    name: "AWS",
    tagline: "EC2 & Lightsail for managed VPS/cloud. Uses IAM access keys.",
    kinds: ["cloud", "vps"],
    docsUrl: "https://docs.aws.amazon.com/ec2/",
    fields: ["api_key", "api_secret", "region"],
  },
  namecheap: {
    name: "Namecheap Hosting",
    tagline: "Shared & EasyWP hosting via reseller API. Pair with domains for bundles.",
    kinds: ["hosting"],
    docsUrl: "https://www.namecheap.com/support/api/intro/",
    fields: ["api_key", "username", "client_id"],
  },
  radwebhosting: {
    name: "Rad Web Hosting",
    tagline: "White-label reseller for shared, cloud & VPS hosting. WHMCS-compatible API.",
    kinds: ["hosting", "vps"],
    docsUrl: "https://radwebhosting.com/clients/knowledgebase",
    fields: ["api_key", "username", "endpoint_url"],
  },
};

const DEFAULT_CONFIG: ProviderConfig = {
  enabled: false,
  sandbox: true,
  markup_percent: 20,
  api_token: "",
  api_key: "",
  api_secret: "",
  username: "",
  client_id: "",
  project_id: "",
  region: "",
  endpoint_url: "",
};

const ALL_IDS = Object.keys(PROVIDER_META) as ProviderId[];

const DEFAULT_SETTINGS: ProvidersSettings = {
  default_vps: "hetzner",
  default_hosting: "namecheap",
  default_cloud: "hetzner",
  providers: ALL_IDS.reduce(
    (acc, id) => ({ ...acc, [id]: { ...DEFAULT_CONFIG } }),
    {} as Record<ProviderId, ProviderConfig>,
  ),
};

const FIELD_LABEL: Record<FieldKey, string> = {
  api_token: "API token",
  api_key: "API key / Access key",
  api_secret: "API secret",
  username: "Username",
  client_id: "Client ID",
  project_id: "Project ID",
  region: "Default region",
  endpoint_url: "API endpoint URL",
};

function HostingProvidersAdmin() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["site_settings", "hosting_providers"],
    queryFn: async () => {
      const { data } = await supabase.from("site_settings").select("value").eq("key", "hosting_providers").maybeSingle();
      const raw = (data?.value as Partial<ProvidersSettings>) || {};
      const merged: ProvidersSettings = {
        default_vps: raw.default_vps || DEFAULT_SETTINGS.default_vps,
        default_hosting: raw.default_hosting || DEFAULT_SETTINGS.default_hosting,
        default_cloud: raw.default_cloud || DEFAULT_SETTINGS.default_cloud,
        providers: ALL_IDS.reduce((acc, id) => {
          acc[id] = { ...DEFAULT_CONFIG, ...(raw.providers?.[id] || {}) };
          return acc;
        }, {} as Record<ProviderId, ProviderConfig>),
      };
      return merged;
    },
  });

  const [state, setState] = useState<ProvidersSettings>(DEFAULT_SETTINGS);
  useEffect(() => { if (data) setState(data); }, [data]);

  const save = useMutation({
    mutationFn: async (value: ProvidersSettings) => {
      const { error } = await supabase.from("site_settings").upsert({ key: "hosting_providers", value: value as any });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["site_settings", "hosting_providers"] });
      toast.success("Hosting provider settings saved");
    },
    onError: (e: any) => toast.error(e.message || "Failed to save"),
  });

  const updateProvider = (id: ProviderId, patch: Partial<ProviderConfig>) => {
    setState((s) => ({ ...s, providers: { ...s.providers, [id]: { ...s.providers[id], ...patch } } }));
  };

  const enabledFor = (kind: Kind) =>
    ALL_IDS.filter((id) => state.providers[id].enabled && PROVIDER_META[id].kinds.includes(kind));

  const stats = useMemo(() => {
    const enabled = ALL_IDS.filter((id) => state.providers[id].enabled);
    const avg = enabled.length
      ? Math.round(enabled.reduce((s, id) => s + (state.providers[id].markup_percent || 0), 0) / enabled.length)
      : 0;
    return {
      enabled: enabled.length,
      vps: enabledFor("vps").length,
      hosting: enabledFor("hosting").length,
      cloud: enabledFor("cloud").length,
      avg,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const [query, setQuery] = useState("");
  const [kindFilter, setKindFilter] = useState<"all" | Kind>("all");
  const filteredIds = ALL_IDS.filter((id) => {
    if (kindFilter !== "all" && !PROVIDER_META[id].kinds.includes(kindFilter)) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return PROVIDER_META[id].name.toLowerCase().includes(q) || id.includes(q);
  });

  const testFn = useServerFn(testHostingProvider);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Hosting & VPS providers"
        description="Resell upstream hosting, VPS and cloud infrastructure. Enable providers, set markup, and pick the default used to provision new orders."
      />

      {isLoading ? (
        <div className="h-40 animate-pulse rounded-xl bg-muted/40" />
      ) : (
        <>
          <StatsStrip
            tiles={[
              { label: "Enabled", value: stats.enabled, icon: CheckCircle2, tone: stats.enabled ? "success" : "warning" },
              { label: "VPS", value: stats.vps, icon: Server, tone: "info" },
              { label: "Hosting", value: stats.hosting, icon: HardDrive },
              { label: "Cloud", value: stats.cloud, icon: Cloud, tone: "info" },
              { label: "Avg markup", value: `${stats.avg}%`, icon: Percent },
            ]}
          />

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Server className="h-5 w-5" /> Default providers</CardTitle>
              <CardDescription>Used as the upstream when a customer places a new order for each category.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-3">
              {(["vps", "hosting", "cloud"] as Kind[]).map((kind) => {
                const key = `default_${kind}` as const;
                const options = enabledFor(kind);
                return (
                  <div key={kind} className="grid gap-1.5">
                    <Label className="capitalize">Default {kind}</Label>
                    <Select
                      value={state[key]}
                      onValueChange={(v) => setState((s) => ({ ...s, [key]: v as ProviderId }))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ALL_IDS.filter((id) => PROVIDER_META[id].kinds.includes(kind)).map((id) => (
                          <SelectItem key={id} value={id} disabled={!state.providers[id].enabled}>
                            {PROVIDER_META[id].name}{state.providers[id].enabled ? "" : " (disabled)"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">{options.length} enabled</p>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-0 flex-1 sm:max-w-xs">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input className="pl-8" placeholder="Filter providers…" value={query} onChange={(e) => setQuery(e.target.value)} />
            </div>
            <div className="flex flex-wrap items-center gap-1">
              {(["all", "vps", "hosting", "cloud"] as const).map((k) => (
                <Button
                  key={k}
                  size="sm"
                  variant={kindFilter === k ? "default" : "outline"}
                  className="h-7 rounded-full px-3 text-xs capitalize"
                  onClick={() => setKindFilter(k)}
                >
                  {k}
                </Button>
              ))}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {filteredIds.map((id) => {
              const meta = PROVIDER_META[id];
              const cfg = state.providers[id];
              return (
                <Card key={id} className={cfg.enabled ? "border-primary/40" : ""}>
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <CardTitle className="flex flex-wrap items-center gap-2">
                          {meta.name}
                          {cfg.enabled && <Badge variant="secondary">Enabled</Badge>}
                          {cfg.sandbox && cfg.enabled && <Badge variant="outline">Sandbox</Badge>}
                        </CardTitle>
                        <CardDescription className="mt-1">{meta.tagline}</CardDescription>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {meta.kinds.map((k) => (
                            <Badge key={k} variant="outline" className="capitalize text-[10px]">{k}</Badge>
                          ))}
                        </div>
                      </div>
                      <Switch checked={cfg.enabled} onCheckedChange={(v) => updateProvider(id, { enabled: v })} />
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {meta.fields.map((f) => (
                      <div key={f} className="grid gap-1.5">
                        <Label>{FIELD_LABEL[f]}</Label>
                        <Input
                          type={f === "api_token" || f === "api_key" || f === "api_secret" ? "password" : "text"}
                          placeholder={f.includes("key") || f.includes("secret") || f.includes("token") ? "••••••••" : ""}
                          value={cfg[f]}
                          onChange={(e) => updateProvider(id, { [f]: e.target.value } as Partial<ProviderConfig>)}
                        />
                      </div>
                    ))}
                    <div className="grid gap-1.5">
                      <Label>Markup over upstream (%)</Label>
                      <Input
                        type="number"
                        min={0}
                        step={1}
                        value={cfg.markup_percent}
                        onChange={(e) => updateProvider(id, { markup_percent: Number(e.target.value) || 0 })}
                      />
                      <p className="text-xs text-muted-foreground">Added to upstream cost when displaying plan prices.</p>
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 px-3 py-2">
                      <div>
                        <p className="text-sm font-medium">Sandbox mode</p>
                        <p className="text-xs text-muted-foreground">Use test endpoints — no real provisioning.</p>
                      </div>
                      <Switch checked={cfg.sandbox} onCheckedChange={(v) => updateProvider(id, { sandbox: v })} />
                    </div>
                    <ProviderTestButton
                      onTest={() =>
                        testFn({
                          data: {
                            provider: id,
                            sandbox: cfg.sandbox,
                            api_token: cfg.api_token,
                            api_key: cfg.api_key,
                            api_secret: cfg.api_secret,
                            username: cfg.username,
                            client_id: cfg.client_id,
                            project_id: cfg.project_id,
                            region: cfg.region,
                            endpoint_url: cfg.endpoint_url,
                          },
                        })
                      }
                    />
                    <a href={meta.docsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                      API docs <ExternalLink className="h-3 w-3" />
                    </a>
                  </CardContent>
                </Card>
              );
            })}
            {filteredIds.length === 0 && (
              <Card className="sm:col-span-2 xl:col-span-3">
                <CardContent className="py-10 text-center text-sm text-muted-foreground">
                  No providers match your filters.
                </CardContent>
              </Card>
            )}
          </div>

          <div className="flex justify-end">
            <Button onClick={() => save.mutate(state)} disabled={save.isPending} size="lg">
              {save.isPending ? "Saving…" : "Save all changes"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
